import { normalizeDate } from './dateUtils';

/**
 * Get daily amount for an employee on a specific date
 * Distributed amount = attendance_amount + production_amount
 */
export const getDailyAmount = (item, dateKey) => {
    if (!item.daily_earnings || !Array.isArray(item.daily_earnings)) {
        return 0;
    }
    
    const normalizedDateKey = normalizeDate(dateKey);
    
    // Try to find matching date - check both exact match and try different formats
    let dayData = item.daily_earnings.find(day => {
        if (!day || !day.date) return false;
        const normalizedDayDate = normalizeDate(day.date);
        return normalizedDayDate === normalizedDateKey;
    });
    
    // If not found, try matching without normalization (direct string comparison)
    if (!dayData) {
        dayData = item.daily_earnings.find(day => {
            if (!day || !day.date) return false;
            // Try direct string match
            return String(day.date).trim() === String(dateKey).trim() || 
                   String(day.date).substring(0, 10) === String(dateKey).substring(0, 10);
        });
    }
    
    if (dayData) {
        // Calculate distributed amount as sum of attendance and production
        // Ensure values are valid numbers, handle "не число" strings
        const attendance = parseFloat(dayData.attendance_amount) || 0;
        const production = parseFloat(dayData.production_amount) || 0;
        const sum = attendance + production;
        return isNaN(sum) ? 0 : sum;
    }
    
    return 0;
};

/**
 * Get salary/wages amount from database (attendance_amount)
 */
export const getExpectedDailyAmount = (item, dateKey) => {
    if (!item.daily_earnings || !Array.isArray(item.daily_earnings)) {
        return 0;
    }
    
    const normalizedDateKey = normalizeDate(dateKey);
    
    // Try to find matching date
    let dayData = item.daily_earnings.find(day => {
        if (!day || !day.date) return false;
        const normalizedDayDate = normalizeDate(day.date);
        return normalizedDayDate === normalizedDateKey;
    });
    
    // If not found, try direct string match
    if (!dayData) {
        dayData = item.daily_earnings.find(day => {
            if (!day || !day.date) return false;
            return String(day.date).trim() === String(dateKey).trim() || 
                   String(day.date).substring(0, 10) === String(dateKey).substring(0, 10);
        });
    }
    
    if (dayData) {
        const amount = parseFloat(dayData.attendance_amount);
        return isNaN(amount) ? 0 : amount;
    }
    return 0;
};

/**
 * Get production amount from database (production_amount)
 */
export const getProductionDailyAmount = (item, dateKey) => {
    if (!item.daily_earnings || !Array.isArray(item.daily_earnings)) {
        return 0;
    }
    
    const normalizedDateKey = normalizeDate(dateKey);
    
    // Try to find matching date
    let dayData = item.daily_earnings.find(day => {
        if (!day || !day.date) return false;
        const normalizedDayDate = normalizeDate(day.date);
        return normalizedDayDate === normalizedDateKey;
    });
    
    // If not found, try direct string match
    if (!dayData) {
        dayData = item.daily_earnings.find(day => {
            if (!day || !day.date) return false;
            return String(day.date).trim() === String(dateKey).trim() || 
                   String(day.date).substring(0, 10) === String(dateKey).substring(0, 10);
        });
    }
    
    if (dayData) {
        const amount = parseFloat(dayData.production_amount);
        return isNaN(amount) ? 0 : amount;
    }
    return 0;
};

/**
 * Calculate daily rate for an employee (using firm's working days per month)
 */
export const getDailyRate = (item, workingDaysPerMonth = 26) => {
    if (item.salary <= 0) return 0;
    return Math.round(item.salary / workingDaysPerMonth);
};

/**
 * Calculate daily totals for a date key
 * Fixed: Now uses calculation functions directly instead of receiving them as parameters
 */
export const getDailyTotals = (dateKey, paginatedData) => {
    let distributedSum = 0;
    let attendanceSum = 0;
    let productionSum = 0;
    
    if (!paginatedData || !Array.isArray(paginatedData)) {
        return { 
            distributedTotal: 0, 
            attendanceTotal: 0, 
            productionTotal: 0 
        };
    }
    
    paginatedData.forEach((item) => {
        if (!item || !item.daily_earnings || !Array.isArray(item.daily_earnings)) {
            return;
        }
        
        // Use functions directly from this module (no circular dependency)
        const distAmount = getDailyAmount(item, dateKey);
        const attAmount = getExpectedDailyAmount(item, dateKey);
        const prodAmount = getProductionDailyAmount(item, dateKey);
        
        distributedSum += distAmount;
        attendanceSum += attAmount;
        productionSum += prodAmount;
    });
    
    return { 
        distributedTotal: distributedSum, 
        attendanceTotal: attendanceSum, 
        productionTotal: productionSum 
    };
};

/**
 * Calculate totals for current page
 */
export const calculatePageTotals = (paginatedData) => {
    return {
        salary: paginatedData.reduce((sum, item) => sum + (item.salary || 0), 0),
        previousRemainder: paginatedData.reduce((sum, item) => sum + (item.previousRemainder || 0), 0),
        processed: paginatedData.reduce((sum, item) => sum + (item.processed || 0), 0),
        earned: paginatedData.reduce((sum, item) => sum + (item.earned || 0), 0),
        paid: paginatedData.reduce((sum, item) => sum + (item.paid || 0), 0),
        currentRemainder: paginatedData.reduce((sum, item) => sum + (item.currentRemainder || 0), 0)
    };
};

